import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'

import { styled, LinearProgress } from '@mui/material'
import useGetYMALItems from '@hooks/useGetYMALItems'
import { getOrder } from '@helpers/checkout/global'
import { addToCart, getCart } from '@helpers/cart'
import useCartVersionChecker from '@hooks/useCartVersionChecker'
import useCartV2 from '@hooks/useCartV2'
import { addToDataLayer_nextgen } from '@helpers/google-tag-manager'
import { IfFeatureEnabled } from '@growthbook/growthbook-react'
import PRODUCT_CAROUSEL from '../../../@rtg2022/components/organisms/ProductCarousel/ProductCarousel'

const LoadingSpinnerWrapper = styled('div')(({ isLoading, displayLoading }) => ({
  height: '1rem',
  margin: '1rem',
  width: 'auto',
  display: 'flex',
  justifyContent: 'center',
  ...(!isLoading ? { width: 0 } : { width: '100%' }),
  ...(!displayLoading && { display: 'none' }),
}))

const StyledLinearProgress = styled(LinearProgress)`
  width: 100%;
`

const StyledProductCarousel = styled(PRODUCT_CAROUSEL)(({ isLoading, displayYMAL, displayLoading }) => ({
  height: 'auto',
  ...(!isLoading
    ? {
        height: !displayLoading ? 'auto' : '280px',
      }
    : {
        height: 0,
      }),
  ...(!displayYMAL && { display: 'none' }),
}))

const StyledRule = styled('hr')`
  margin: 1rem 0 !important;
`
function YouMayAlsoLikeWrapper({ product, ...props }) {
  const cart = getCart()
  const order = getOrder()
  const { data: carouselSlidesData, isLoading, isError } = useGetYMALItems(product)
  const showRule = !isLoading && !isEmpty(carouselSlidesData)
  const rtg_location = useSelector(state => state.location.rtg_location)

  const { addCartLineItemsMutation, cart: cartV2 } = useCartV2()
  const cartVersion = useCartVersionChecker()

  const loadingRef = useRef()
  const [displayLoading, setDisplayLoading] = useState(isLoading)

  useEffect(() => {
    if (!cartVersion) return
    if (loadingRef.current && !isLoading) {
      loadingRef.current.style.height = '0px'
      loadingRef.current.style.margin = '0px'
      setTimeout(() => {
        setDisplayLoading(isLoading)
      }, 1000)
    }
  }, [carouselSlidesData, isLoading, cartVersion])

  const ymalFeatureId = 'you-may-also-like-atc'

  const cartTotal = cartVersion === 'v1' ? order?.total : cartV2?.cartTotal
  const cartItems =
    cartVersion === 'v1'
      ? cart?.cartItems?.map(({ sku, quantity, price }) => ({ sku, quantity, price })) ?? []
      : cartV2?.lineItems?.map(({ sku, quantity, unitPrice }) => ({ sku, quantity, price: unitPrice })) ?? []

  return (
    <>
      {!carouselSlidesData?.length && <div style={{ margin: '15px 0' }} />}
      {/* Growthbook A/B test, will render for 50% of users based on sessionID */}
      {/* <IfFeatureEnabled feature={ymalFeatureId}> */}
      {/* Prevent divider from showing when served YMAL but no products fetched */}
      {showRule && <StyledRule />}
      <LoadingSpinnerWrapper
        ref={loadingRef}
        id="you-may-also-like__loading__cart"
        isLoading={isLoading}
        displayLoading={displayLoading}
        hidden
      >
        {!isError && <StyledLinearProgress />}
      </LoadingSpinnerWrapper>
      <StyledProductCarousel
        id="you-may-also-like__cart"
        type="add-to-cart-modal"
        title="YOU MAY ALSO LIKE"
        isLoading={isLoading && !carouselSlidesData?.length}
        displayLoading={displayLoading}
        displayYMAL={!!carouselSlidesData?.length || carouselSlidesData === undefined}
        trigger_gtm={addToDataLayer_nextgen}
        price_zone={`${rtg_location.region}_${rtg_location.price_zone}`}
        slides={carouselSlidesData?.length ? carouselSlidesData : []}
        addToCart={addToCart}
        addCartLineItemsMutation={cartVersion === 'v1' ? undefined : addCartLineItemsMutation}
        cartItems={cartItems}
        cartTotal={cartTotal}
        modalHeight={props.modalHeight}
      />
      {/* </IfFeatureEnabled> */}
    </>
  )
}

YouMayAlsoLikeWrapper.propTypes = {
  product: PropTypes.any,
  modalHeight: PropTypes.number,
}

export default YouMayAlsoLikeWrapper
